/**
 * "How to get your key": an expandable, step-by-step guide for one provider.
 *
 * All wording lives in `providerSetupGuides.ts`. Each disclosure is a native
 * `<button>` (Enter and Space work, it is in the tab order) carrying
 * `aria-expanded` and `aria-controls`; the panel it controls is always in the
 * document and only `hidden` while closed. Every outside link opens in a new tab
 * with `rel="noopener noreferrer"`. Nothing here reads, holds or shows a key.
 */

import React, { useEffect, useId, useState } from 'react';

import {
  GETTING_STARTED,
  GUIDE_TEXT,
  guideFor,
  type GuideLink,
  type ProviderSetupGuide as Guide,
} from './providerSetupGuides';
import {
  CONNECTION_GUIDE_TEXT,
  connectionGuideFor,
  type ConnectionSetupGuide as ConnectionGuide,
} from './connectionSetupGuides';

export function ExternalLink({ link }: { link: GuideLink }): React.ReactElement {
  return (
    <a href={link.href} target="_blank" rel="noopener noreferrer">
      {link.label}
      <span className="visually-hidden"> {GUIDE_TEXT.newTab}</span>
    </a>
  );
}

/** A controlled disclosure: a button that shows or hides the region below it. */
export function Disclosure({ id, label, open, onToggle, className, children }: {
  id: string;
  label: string;
  open: boolean;
  onToggle: () => void;
  className?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className={className}>
      <button
        type="button"
        className="setup-guide-toggle"
        aria-expanded={open}
        aria-controls={id}
        onClick={onToggle}
      >
        <span aria-hidden="true">{open ? '▾' : '▸'}</span> {label}
      </button>
      <div id={id} className="setup-guide-body" hidden={!open}>
        {children}
      </div>
    </div>
  );
}

export type GuideContext = 'keys' | 'connections';

/** Headings that fit where the guide sits: under an h2 in the key form, under an h3 in Connections. */
function headingTags(context: GuideContext): { title: 'h3' | 'h4'; section: 'h4' | 'h5' } {
  return context === 'keys' ? { title: 'h3', section: 'h4' } : { title: 'h4', section: 'h5' };
}

/** The guide's content, with its open states passed in (rendered directly by tests). */
export function ProviderSetupGuideView({ guide, idBase, open, newOpen, onToggle, onToggleNew, context, verification }: {
  guide: Guide;
  idBase: string;
  open: boolean;
  newOpen: boolean;
  onToggle: () => void;
  onToggleNew: () => void;
  context: GuideContext;
  /** The server catalog's verification for this provider; `not_available` means no automatic check. */
  verification?: string | null;
}): React.ReactElement {
  const titleId = `${idBase}-title`;
  const { title: Title, section: Section } = headingTags(context);
  return (
    <Disclosure
      id={`${idBase}-body`}
      label={`${GUIDE_TEXT.toggle} (${guide.name})`}
      open={open}
      onToggle={onToggle}
      className="setup-guide"
    >
      <section aria-labelledby={titleId} data-provider-guide={guide.id}>
        <Title id={titleId} className="setup-guide-title">{guide.name}: {GUIDE_TEXT.toggle.toLowerCase()}</Title>
        <p className="setup-guide-powers"><strong>What it powers in Auteur:</strong> {guide.powers}</p>

        <Disclosure
          id={`${idBase}-new`}
          label={GUIDE_TEXT.newToThisToggle}
          open={newOpen}
          onToggle={onToggleNew}
          className="setup-guide-new"
        >
          <p>{GUIDE_TEXT.whatIsAKey}</p>
          {guide.signUp ? <p>{GUIDE_TEXT.noAccount} <ExternalLink link={guide.signUp} /></p> : null}
          {guide.billingPage ? <p>{GUIDE_TEXT.addCredit} <ExternalLink link={guide.billingPage} /></p> : null}
          {guide.firstUseCost ? <p>{guide.firstUseCost}</p> : null}
          {guide.pricingPage ? <p>{GUIDE_TEXT.firstUseCostFallback} <ExternalLink link={guide.pricingPage} /></p> : null}
        </Disclosure>

        <ol className="setup-guide-steps">
          {guide.steps.map((step, index) => (
            <li key={index}>
              {step.text}{step.link ? <>{' '}<ExternalLink link={step.link} /></> : null}
            </li>
          ))}
        </ol>

        <Section>{GUIDE_TEXT.whoPaysTitle}</Section>
        <p>{guide.billing} {GUIDE_TEXT.whoPaysCommon}</p>

        <Section>{GUIDE_TEXT.pasteTitle}</Section>
        <p>{context === 'keys' ? GUIDE_TEXT.paste : GUIDE_TEXT.pasteFromConnections}</p>

        <Section>{GUIDE_TEXT.checkTitle}</Section>
        <p>{verification === 'not_available' ? GUIDE_TEXT.checkNoProbe : GUIDE_TEXT.checkProbe}</p>

        <Section>{GUIDE_TEXT.proTitle}</Section>
        <p>
          {guide.keyPage ? <><ExternalLink link={guide.keyPage} />. </> : null}
          {guide.pro}
        </p>

        <Section>{GUIDE_TEXT.safetyTitle}</Section>
        <ul className="setup-guide-safety">
          {GUIDE_TEXT.safety.map((tip) => <li key={tip}>{tip}</li>)}
          {guide.revokePage ? <li>{GUIDE_TEXT.revokeLinkLabel}: <ExternalLink link={guide.revokePage} /></li> : null}
        </ul>

        {guide.helpPage ? (
          <p className="setup-guide-help">{GUIDE_TEXT.helpTitle}: <ExternalLink link={guide.helpPage} /></p>
        ) : null}
      </section>
    </Disclosure>
  );
}

/** The guide for one server-listed provider id; renders nothing when there is no guide. */
export function ProviderSetupGuide({ providerId, context = 'keys', verification = null, defaultOpen = false }: {
  providerId: string | null | undefined;
  context?: GuideContext;
  verification?: string | null;
  /** Open the guide when it is the target of a workspace "How to unlock" link. */
  defaultOpen?: boolean;
}): React.ReactElement | null {
  const reactId = useId();
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => { if (defaultOpen) setOpen(true); }, [defaultOpen, providerId]);
  const [newOpen, setNewOpen] = useState(false);
  const guide = guideFor(providerId);
  if (!guide) return null;
  const idBase = `setup-guide-${guide.id}-${reactId.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  return (
    <ProviderSetupGuideView
      guide={guide}
      idBase={idBase}
      open={open}
      newOpen={newOpen}
      onToggle={() => setOpen((value) => !value)}
      onToggleNew={() => setNewOpen((value) => !value)}
      context={context}
      verification={verification}
    />
  );
}

/** The onboarding "Getting started" overview: which key matters for which kind of work. */
export function GettingStartedOverview(): React.ReactElement {
  return (
    <section className="setup-getting-started" aria-labelledby="setup-getting-started-title">
      <h3 id="setup-getting-started-title">{GETTING_STARTED.title}</h3>
      <p>{GETTING_STARTED.intro}</p>
      <dl>
        {GETTING_STARTED.rows.map((row) => (
          <React.Fragment key={row.goal}>
            <dt>{row.goal}</dt>
            <dd>{row.providers}</dd>
          </React.Fragment>
        ))}
      </dl>
      <p>{GETTING_STARTED.footer}</p>
    </section>
  );
}

/** "What is this and how do I connect it?" for a Connections row that is not an API key. */
export function ConnectionSetupGuideView({ guide, idBase, open, onToggle }: {
  guide: ConnectionGuide;
  idBase: string;
  open: boolean;
  onToggle: () => void;
}): React.ReactElement {
  return (
    <Disclosure id={`${idBase}-body`} label={CONNECTION_GUIDE_TEXT.toggle} open={open} onToggle={onToggle} className="setup-guide">
      <section data-connection-guide={guide.id}>
        <h4>{CONNECTION_GUIDE_TEXT.what}</h4>
        <p>{guide.what}</p>
        <h4>{CONNECTION_GUIDE_TEXT.needIt}</h4>
        <p>{guide.needIt}</p>
        <h4>{CONNECTION_GUIDE_TEXT.whereToGo}</h4>
        <p>{guide.whereToGo}</p>
        {guide.links.length > 0 ? (
          <>
            <h4>{CONNECTION_GUIDE_TEXT.links}</h4>
            <ul className="setup-guide-safety">
              {guide.links.map((link) => <li key={link.href}><ExternalLink link={link} /></li>)}
            </ul>
          </>
        ) : null}
      </section>
    </Disclosure>
  );
}

/** The guide for one Connections row id; renders nothing when there is none. */
export function ConnectionSetupGuide({ entryId }: { entryId: string }): React.ReactElement | null {
  const reactId = useId();
  const [open, setOpen] = useState(false);
  const guide = connectionGuideFor(entryId);
  if (!guide) return null;
  return (
    <ConnectionSetupGuideView
      guide={guide}
      idBase={`connection-guide-${guide.id}-${reactId.replace(/[^a-zA-Z0-9_-]/g, '')}`}
      open={open}
      onToggle={() => setOpen((value) => !value)}
    />
  );
}
